/* eslint-disable */
/**
 * Phase 98k v2 — Écran de réconciliation admin post-tournée (format Nayax-like).
 *
 * Layout tableau par distributeur avec :
 *  - Entête à droite : Pick date, Heure passage, Nb unités, Nettoyé, État machine
 *  - Colonnes : Slot | Produit | Prix (affiché) | Contenu réel | Reappro | Note
 *
 * L'opérateur remplit la machine et écrit sur papier :
 *   - Ce qu'il a trouvé (contenu réel)
 *   - Combien il a ajouté (reappro)
 *   - Une éventuelle note (sortie, cassé, etc.)
 *   - Heure de passage, s'il a nettoyé, état général
 *
 * L'admin retranscrit ici. Le backend :
 *   - Calcule delta_shipped = qty_shipped - reappro
 *   - Si delta > 0 : mouvement WASTE auto sur le distributeur
 *   - Persiste heure passage / nettoyage / état machine pour audit
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CheckCircle2, Loader2, AlertCircle, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

export default function RefillTourReconcile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [tour, setTour] = useState(null);
  // Map par (distrib::product::slot) → { contenu_reel, reappro, note }
  const [lineMap, setLineMap] = useState({});
  // Map par distributor_id → { heure_passage, nettoye, etat_machine, note_generale }
  const [headerMap, setHeaderMap] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/refill-tours/${id}`);
        setTour(data);
        const initLines = {};
        const initHeaders = {};
        for (const ln of data.lines || []) {
          const qty_shipped = Number(ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0);
          if (qty_shipped <= 0) continue;
          const key = `${ln.distributor_id}::${ln.product_id}::${ln.slot_number || ""}`;
          initLines[key] = {
            contenu_reel: Number(ln.current_qty || 0),  // pré-rempli avec snapshot
            reappro: qty_shipped,                         // pré-rempli à qty_shipped
            note: ln.return_notes || "",
          };
          if (!initHeaders[ln.distributor_id]) {
            initHeaders[ln.distributor_id] = {
              heure_passage: "",
              nettoye: false,
              etat_machine: "",
              note_generale: "",
            };
          }
        }
        setLineMap(initLines);
        setHeaderMap(initHeaders);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur chargement");
        navigate("/tournees-reappro");
      }
    })();
  }, [id, navigate]);

  const distribGroups = useMemo(() => {
    if (!tour) return [];
    const grp = {};
    for (const ln of tour.lines || []) {
      const qty = Number(ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0);
      if (qty <= 0) continue;
      const did = ln.distributor_id;
      if (!grp[did]) {
        grp[did] = {
          distributor_id: did,
          distributor_name: ln.distributor_name,
          distributor_code: ln.distributor_code,
          distributor_city: ln.distributor_city,
          lines: [],
        };
      }
      grp[did].lines.push(ln);
    }
    const ordered = (tour.selected_distributor_ids || []).map((did) => grp[did]).filter(Boolean);
    Object.keys(grp).forEach((did) => {
      if (!(tour.selected_distributor_ids || []).includes(did)) ordered.push(grp[did]);
    });
    ordered.forEach((g) => g.lines.sort((a, b) => {
      const sa = parseInt(String(a.slot_number || "9999").split(/[,/]/)[0], 10);
      const sb = parseInt(String(b.slot_number || "9999").split(/[,/]/)[0], 10);
      if (sa !== sb) return sa - sb;
      return (a.product_name || "").localeCompare(b.product_name || "");
    }));
    return ordered;
  }, [tour]);

  const updateLine = (distributor_id, product_id, slot_number, field, value) => {
    const key = `${distributor_id}::${product_id}::${slot_number || ""}`;
    setLineMap((m) => ({ ...m, [key]: { ...m[key], [field]: value } }));
  };

  const updateHeader = (distributor_id, field, value) => {
    setHeaderMap((m) => ({ ...m, [distributor_id]: { ...m[distributor_id], [field]: value } }));
  };

  // Totaux
  const totals = useMemo(() => {
    let planned = 0, reappro = 0, deltaMissing = 0, deltaExtra = 0;
    for (const g of distribGroups) {
      for (const ln of g.lines) {
        const key = `${ln.distributor_id}::${ln.product_id}::${ln.slot_number || ""}`;
        const r = lineMap[key] || {};
        const shipped = Number(ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0);
        const reapp = Number(r.reappro || 0);
        const delta = shipped - reapp;
        planned += shipped;
        reappro += reapp;
        if (delta > 0.001) deltaMissing += delta;
        if (delta < -0.001) deltaExtra += -delta;
      }
    }
    return {
      planned: Math.round(planned * 100) / 100,
      reappro: Math.round(reappro * 100) / 100,
      deltaMissing: Math.round(deltaMissing * 100) / 100,
      deltaExtra: Math.round(deltaExtra * 100) / 100,
    };
  }, [distribGroups, lineMap]);

  const submitReconcile = async () => {
    const lines = [];
    for (const g of distribGroups) {
      for (const ln of g.lines) {
        const key = `${ln.distributor_id}::${ln.product_id}::${ln.slot_number || ""}`;
        const r = lineMap[key] || {};
        lines.push({
          distributor_id: ln.distributor_id,
          product_id: ln.product_id,
          slot_number: ln.slot_number || "",
          contenu_reel: Number(r.contenu_reel || 0),
          reappro: Number(r.reappro || 0),
          note: (r.note || "").trim(),
        });
      }
    }
    const headers = Object.entries(headerMap).map(([distributor_id, h]) => ({
      distributor_id,
      heure_passage: (h.heure_passage || "").trim(),
      nettoye: Boolean(h.nettoye),
      etat_machine: (h.etat_machine || "").trim(),
      note_generale: (h.note_generale || "").trim(),
    }));

    const nbAnomalies = totals.deltaMissing + totals.deltaExtra;
    const desc = nbAnomalies < 0.001
      ? "Aucun écart détecté. La tournée sera clôturée sans mouvement de stock supplémentaire."
      : `Écarts détectés :\n• ${totals.deltaMissing} barquette(s) manquante(s) → WASTE auto sur les distribs concernés\n• ${totals.deltaExtra} barquette(s) en plus (anomalies loggées)\n\nLa tournée passe en CLOTUREE.`;

    const ok = await confirm({
      title: nbAnomalies < 0.001 ? "Clôturer sans écart ?" : "Confirmer la réconciliation ?",
      description: desc,
      confirmText: "Réconcilier & clôturer",
      destructive: nbAnomalies > 0.001,
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      const { data } = await api.post(`/refill-tours/${id}/reconcile`, { lines, headers });
      const nbMv = (data.missing_movements || []).length;
      const nbAn = (data.anomalies || []).length;
      const nbW = (data.warnings || []).length;
      toast.success(
        `Tournée réconciliée : ${data.reconciled_lines} slots traités` +
        (nbMv > 0 ? ` · ${nbMv} manquants WASTE` : "") +
        (nbAn > 0 ? ` · ${nbAn} anomalies` : "") +
        (nbW > 0 ? ` · ${nbW} avertissement(s)` : "")
      );
      if (data.warnings?.length) {
        data.warnings.forEach((w) => toast.warning(w));
      }
      navigate(`/tournees-reappro/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur réconciliation");
    } finally {
      setSubmitting(false);
    }
  };

  if (!tour) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline" /> Chargement…
      </div>
    );
  }

  if (tour.status === "CLOTUREE") {
    return (
      <div className="p-8 text-center space-y-3" data-testid="tour-already-closed">
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#1F4E3D]" />
        <p className="text-lg font-medium">Cette tournée est déjà clôturée.</p>
        <Button onClick={() => navigate(`/tournees-reappro/${id}`)}>Voir le détail</Button>
      </div>
    );
  }

  if (tour.status !== "EN_LIVRAISON") {
    return (
      <div className="p-8 text-center space-y-3">
        <AlertCircle className="w-12 h-12 mx-auto text-[#C05C47]" />
        <p className="text-lg font-medium">
          La réconciliation ne peut être saisie qu'après verrouillage (statut EN_LIVRAISON).
        </p>
        <p className="text-sm text-[#6B6E6B]">
          Statut actuel : <strong>{tour.status}</strong>
        </p>
        <Button onClick={() => navigate(`/tournees-reappro/${id}`)}>Retour au détail</Button>
      </div>
    );
  }

  const pickDate = tour.locked_at ? String(tour.locked_at).slice(0, 10) : "—";

  return (
    <div className="space-y-5 p-4" data-testid="reconcile-page">
      {/* Header page */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate(`/tournees-reappro/${id}`)} data-testid="reconcile-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-[#1F4E3D]" />
          <h1 className="text-xl font-bold font-mono">{tour.reference}</h1>
          <Badge className="bg-[#EAF1F8] text-[#2E5C8A] border-[#C5D9E8]">Réconciliation</Badge>
        </div>
        <div className="flex-1" />
        <Button
          onClick={submitReconcile}
          disabled={submitting}
          className="bg-[#1F4E3D] hover:bg-[#173D2F]"
          data-testid="reconcile-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Réconcilier & clôturer
        </Button>
      </div>

      {/* Totaux résumé */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Prévu (sorti dépôt)" value={totals.planned} color="#1A1C1A" />
        <StatCard label="Reappro saisi" value={totals.reappro} color="#1F4E3D" />
        <StatCard label="Manquant" value={totals.deltaMissing} color="#C05C47" warn={totals.deltaMissing > 0} />
        <StatCard label="En plus" value={totals.deltaExtra} color="#B48A00" warn={totals.deltaExtra > 0} />
      </div>

      <div className="text-xs text-[#6B6E6B] leading-relaxed bg-[#F5F5F0] p-3 border-l-4 border-[#C1A063]">
        <strong>Comment saisir :</strong> reportez les notes crayon de l'opérateur.
        Une ligne par slot : <em>Contenu réel</em> (ce qu'il a trouvé dans le slot en arrivant)
        + <em>Reappro</em> (ce qu'il a ajouté). Colonne <em>Écart</em> auto-calculée.
        Entête à droite : heure passage, nettoyage, état machine. Pré-remplissage "tout comme
        prévu" — ne touchez que si écart.
      </div>

      {/* Groupes par distributeur */}
      {distribGroups.map((g) => {
        const gLines = g.lines;
        const totalUnits = gLines.reduce((s, ln) => {
          const key = `${ln.distributor_id}::${ln.product_id}::${ln.slot_number || ""}`;
          return s + Number((lineMap[key] || {}).reappro || 0);
        }, 0);
        const h = headerMap[g.distributor_id] || {};
        return (
          <div key={g.distributor_id} className="border border-[#E5E5E0] bg-white overflow-hidden" data-testid={`dist-group-${g.distributor_id}`}>
            <div className="bg-[#1F4E3D] text-white px-4 py-2 flex items-center gap-2 flex-wrap">
              <span className="font-mono font-semibold">{g.distributor_code}</span>
              <span className="font-medium">{g.distributor_name}</span>
              <span className="text-xs opacity-80">· {g.distributor_city}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-0">
              {/* Table lignes (gauche) */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAF7]">
                    <tr>
                      <th className="text-left px-3 py-2 w-16">Slot</th>
                      <th className="text-left px-3 py-2">Produit</th>
                      <th className="text-right px-3 py-2 w-20 hidden sm:table-cell">Prix</th>
                      <th className="text-center px-3 py-2 w-20">Prévu</th>
                      <th className="text-center px-3 py-2 w-24">Contenu réel</th>
                      <th className="text-center px-3 py-2 w-24">Reappro</th>
                      <th className="text-center px-3 py-2 w-16">Écart</th>
                      <th className="text-left px-3 py-2 min-w-[140px]">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gLines.map((ln) => {
                      const key = `${ln.distributor_id}::${ln.product_id}::${ln.slot_number || ""}`;
                      const r = lineMap[key] || { contenu_reel: 0, reappro: 0, note: "" };
                      const shipped = Number(ln.qty_shipped || ln.qty_loaded || ln.qty_planned || 0);
                      const delta = shipped - Number(r.reappro || 0);
                      const deltaColor = delta > 0.001 ? "text-[#C05C47] font-semibold" : delta < -0.001 ? "text-[#B48A00] font-semibold" : "text-[#6B6E6B]";
                      const priceLabel = ln.unit_price != null
                        ? `${Number(ln.unit_price).toFixed(2)}`
                        : (ln.price_distributor != null ? Number(ln.price_distributor).toFixed(2) : "—");
                      return (
                        <tr key={key} className="border-t border-[#E5E5E0]" data-testid={`recon-row-${key}`}>
                          <td className="px-3 py-2 font-mono font-semibold">{ln.slot_number || "—"}</td>
                          <td className="px-3 py-2">{ln.product_name || ln.product_id}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-[#6B6E6B] hidden sm:table-cell">{priceLabel}</td>
                          <td className="px-3 py-2 text-center font-mono font-semibold">{shipped}</td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              value={r.contenu_reel}
                              onChange={(e) => updateLine(ln.distributor_id, ln.product_id, ln.slot_number, "contenu_reel", e.target.value === "" ? 0 : Number(e.target.value))}
                              className="text-center h-9 font-mono"
                              data-testid={`contenu-reel-${key}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              value={r.reappro}
                              onChange={(e) => updateLine(ln.distributor_id, ln.product_id, ln.slot_number, "reappro", e.target.value === "" ? 0 : Number(e.target.value))}
                              className="text-center h-9 font-mono"
                              data-testid={`reappro-${key}`}
                            />
                          </td>
                          <td className={`px-3 py-2 text-center font-mono ${deltaColor}`}>
                            {Math.abs(delta) < 0.001 ? "0" : (delta > 0 ? `-${delta}` : `+${-delta}`)}
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="text"
                              placeholder={Math.abs(delta) > 0.001 ? "Cassé / refusé / sortie…" : ""}
                              value={r.note}
                              onChange={(e) => updateLine(ln.distributor_id, ln.product_id, ln.slot_number, "note", e.target.value)}
                              className="h-9 text-xs"
                              data-testid={`note-${key}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Entête à droite */}
              <div className="border-l border-[#E5E5E0] p-3 bg-[#FAFAF7] space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Pick date</div>
                  <div className="text-sm font-mono">{pickDate}</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Heure de passage</label>
                  <Input
                    type="time"
                    value={h.heure_passage || ""}
                    onChange={(e) => updateHeader(g.distributor_id, "heure_passage", e.target.value)}
                    className="h-9 font-mono mt-1"
                    data-testid={`heure-${g.distributor_id}`}
                  />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Nombre d'unités</div>
                  <div className="text-lg font-mono font-semibold text-[#1F4E3D]">{totalUnits}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`nettoye-${g.distributor_id}`}
                    checked={Boolean(h.nettoye)}
                    onCheckedChange={(v) => updateHeader(g.distributor_id, "nettoye", Boolean(v))}
                    data-testid={`nettoye-${g.distributor_id}`}
                  />
                  <label htmlFor={`nettoye-${g.distributor_id}`} className="text-sm cursor-pointer select-none">
                    Nettoyé
                  </label>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">État machine</label>
                  <Input
                    type="text"
                    placeholder="OK / porte grince / voyant HS…"
                    value={h.etat_machine || ""}
                    onChange={(e) => updateHeader(g.distributor_id, "etat_machine", e.target.value)}
                    className="h-9 text-xs mt-1"
                    data-testid={`etat-${g.distributor_id}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Note générale</label>
                  <textarea
                    value={h.note_generale || ""}
                    onChange={(e) => updateHeader(g.distributor_id, "note_generale", e.target.value)}
                    rows={2}
                    placeholder="Info libre pour cette machine"
                    className="w-full text-xs border border-[#E5E5E0] p-2 mt-1 rounded resize-none focus:outline-none focus:border-[#1F4E3D]"
                    data-testid={`note-generale-${g.distributor_id}`}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* CTA sticky bas */}
      <div className="sticky bottom-0 bg-white border-t border-[#E5E5E0] py-3 flex items-center justify-end gap-3 -mx-4 px-4">
        <div className="text-sm text-[#6B6E6B] mr-auto">
          {totals.deltaMissing + totals.deltaExtra < 0.001
            ? "Aucun écart — cliquez pour clôturer"
            : `${(totals.deltaMissing + totals.deltaExtra).toFixed(2)} barquette(s) d'écart total`}
        </div>
        <Button
          onClick={submitReconcile}
          disabled={submitting}
          className="bg-[#1F4E3D] hover:bg-[#173D2F]"
          data-testid="reconcile-submit-sticky-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Réconcilier & clôturer
        </Button>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, warn }) {
  return (
    <div
      className="border p-3"
      style={{
        borderColor: warn ? color : "#E5E5E0",
        background: warn ? `${color}0F` : "white",
      }}
    >
      <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">{label}</div>
      <div className="text-2xl font-mono font-semibold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
